import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ClipboardDocumentCheckIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  BuildingOffice2Icon,
  CalendarIcon,
  UserIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useConferencias } from '../hooks/useConferencias';
import type { ConferenciaListItem, StatusConferencia } from '../types/conferencia';
import DivergenciaResumo from '../components/conferencia/DivergenciaResumo';
import { formatDateTime } from '../utils/format';
import { inputIris, btnPrimario, btnSecundario } from '../components/ui/iris';

/** ConferenciaNfe.Status → rótulo + tom do badge (só 2 valores, ver types/conferencia.ts). */
const STATUS_CONFERENCIA: Record<StatusConferencia, { label: string; badge: string }> = {
  em_andamento: { label: 'Em andamento', badge: 'bg-accent-soft text-accent-text' },
  concluida: { label: 'Concluída', badge: 'bg-ok-soft text-ok' },
};

/**
 * Badge da linha: pendência (não resolvida) e pendência resolvida têm precedência sobre
 * "Concluída" simples — a lista não traz `temPendencia`/`pendenciaResolvida` derivados
 * (só o detalhe traz), então deriva aqui a partir de `pendenciaCategorias`/`pendenciaResolvidaEm`.
 */
function badgeConferencia(c: {
  status: StatusConferencia;
  pendenciaCategorias: string | null;
  pendenciaResolvidaEm: string | null;
}) {
  const temPend = !!c.pendenciaCategorias;
  if (c.status === 'concluida' && temPend && !c.pendenciaResolvidaEm) {
    return { label: 'Pendência', badge: 'bg-warning-soft text-warning' };
  }
  if (c.status === 'concluida' && temPend && c.pendenciaResolvidaEm) {
    return { label: 'Pendência resolvida', badge: 'bg-ok-soft text-ok' };
  }
  return STATUS_CONFERENCIA[c.status];
}

/** Chips de filtro de status — 'undefined' = Todas. */
const STATUS_CHIPS: { label: string; value: StatusConferencia | undefined }[] = [
  { label: 'Todas', value: undefined },
  { label: 'Em andamento', value: 'em_andamento' },
  { label: 'Concluídas', value: 'concluida' },
];

/** KPI Íris (desktop): eyebrow maiúsculo + número grande tabular — mesmo molde de XmlTerceirosPage. */
const Kpi: React.FC<{ label: string; value: React.ReactNode; valueClass?: string }> = ({
  label, value, valueClass = 'text-tx',
}) => (
  <div className="flex flex-col justify-center rounded-iris border border-line bg-surface px-3.5 py-2.5">
    <span className="text-[10px] font-medium uppercase tracking-wide text-tx-3">{label}</span>
    <span className={`mt-0.5 text-2xl font-bold tabular-nums ${valueClass}`}>{value}</span>
  </div>
);

/** ISO date → é hoje, em horário local (usado pro KPI "Concluídas hoje"). */
const isHoje = (iso: string | null): boolean => {
  if (!iso) return false;
  const d = new Date(iso);
  const hoje = new Date();
  return (
    d.getFullYear() === hoje.getFullYear() &&
    d.getMonth() === hoje.getMonth() &&
    d.getDate() === hoje.getDate()
  );
};

const temDivergencia = (c: ConferenciaListItem): boolean =>
  c.resumo.falta > 0 || c.resumo.excedente > 0 || c.resumo.avaria > 0;

const ConferenciaListPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusFiltro, setStatusFiltro] = useState<StatusConferencia | undefined>(undefined);
  const [somentePendencia, setSomentePendencia] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Debounce da busca — mesmo padrão de XmlTerceirosPage, ainda que o filtro seja local
  // (a lista inteira já vem numa única chamada, sem paginação server-side no contrato).
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim().toLowerCase()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Sem filtro no servidor: os KPIs precisam da janela inteira independente do chip
  // ativo, então busca uma vez e filtra status/busca no cliente (idem CotacoesPage).
  const { data: conferencias, isLoading, error, refetch, isFetching } = useConferencias();
  const lista = conferencias ?? [];

  const resumo = useMemo(
    () => ({
      emAndamento: lista.filter((c) => c.status === 'em_andamento').length,
      concluidasHoje: lista.filter((c) => c.status === 'concluida' && isHoje(c.concluidoEm)).length,
      comDivergencia: lista.filter(temDivergencia).length,
    }),
    [lista]
  );

  const listaFiltrada = useMemo(() => {
    let arr = lista;
    if (statusFiltro) arr = arr.filter((c) => c.status === statusFiltro);
    // Client-side (mesmo motivo do status acima: a lista inteira já veio numa única chamada
    // pros KPIs, e o backend só ganha o param `pendencia` pra consumo server-side dedicado
    // — a tela de Pendências, D1). "Com pendência" aqui = tem e ainda não foi resolvida.
    if (somentePendencia) arr = arr.filter((c) => !!c.pendenciaCategorias && !c.pendenciaResolvidaEm);
    if (searchTerm) {
      arr = arr.filter((c) =>
        [c.numeroNfe, c.fornecedorNome, c.setorRecebimento, c.usuario, c.chaveNfe]
          .some((campo) => (campo ?? '').toLowerCase().includes(searchTerm))
      );
    }
    return arr;
  }, [lista, statusFiltro, somentePendencia, searchTerm]);

  const limparTudo = () => {
    setSearchInput('');
    setSearchTerm('');
    setStatusFiltro(undefined);
    setSomentePendencia(false);
  };

  const chip = (ativo: boolean) =>
    `h-9 flex-shrink-0 rounded-iris px-3 text-sm font-medium transition-colors ${
      ativo ? 'bg-accent text-white' : 'bg-surface-2 text-tx-2 hover:bg-surface'
    }`;

  if (error) {
    return (
      <div className="mx-auto max-w-[1280px] space-y-4">
        <div className="rounded-iris-lg border border-danger-border bg-danger-soft p-4">
          <h3 className="text-sm font-semibold text-danger">Erro ao carregar conferências</h3>
          <p className="mt-1 text-sm text-tx-2">Não foi possível carregar a lista. Tente atualizar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] space-y-4">
      {/* Header canônico Íris */}
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-xl font-semibold tracking-[-0.01em] text-tx">
            <ClipboardDocumentCheckIcon className="h-6 w-6 flex-shrink-0 text-accent-text" />
            Conferência de NF-e
          </h1>
          <p className="mt-0.5 text-sm text-tx-3">Bipagem e conferência de mercadoria recebida.</p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-iris px-2.5 text-sm font-medium text-tx-2 transition-colors hover:bg-surface-2 disabled:opacity-50"
            title="Atualizar lista"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Atualizar</span>
          </button>
          <Link to="/conferencia/pendencias" className={`${btnSecundario} px-3 sm:px-4`}>
            <ExclamationTriangleIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Pendências</span>
            <span className="sm:hidden">Pend.</span>
          </Link>
          <button
            onClick={() => navigate('/conferencia/nova')}
            className={`${btnPrimario} px-3 sm:px-4`}
          >
            <PlusIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Nova conferência</span>
            <span className="sm:hidden">Nova</span>
          </button>
        </div>
      </header>

      {/* KPIs — cards no desktop; mini-tira no mobile. */}
      <div className="hidden gap-2.5 sm:grid sm:grid-cols-3">
        <Kpi label="Em andamento" value={resumo.emAndamento} valueClass="text-accent-text" />
        <Kpi label="Concluídas hoje" value={resumo.concluidasHoje} valueClass="text-ok" />
        <Kpi label="Com divergência" value={resumo.comDivergencia} valueClass="text-warning" />
      </div>
      <div className="flex items-baseline gap-x-4 rounded-iris border border-line bg-surface px-3.5 py-2 sm:hidden">
        <span>
          <span className="text-base font-bold tabular-nums text-accent-text">{resumo.emAndamento}</span>
          <span className="ml-1 text-[10px] uppercase tracking-wide text-tx-3">andamento</span>
        </span>
        <span>
          <span className="text-base font-bold tabular-nums text-ok">{resumo.concluidasHoje}</span>
          <span className="ml-1 text-[10px] uppercase tracking-wide text-tx-3">hoje</span>
        </span>
        <span className="ml-auto">
          <span className="text-base font-bold tabular-nums text-warning">{resumo.comDivergencia}</span>
          <span className="ml-1 text-[10px] uppercase tracking-wide text-tx-3">diverg.</span>
        </span>
      </div>

      {/* Deck de controle: busca + status + Limpar */}
      <div className="rounded-iris-lg border border-line bg-surface p-3 sm:p-4">
        <div className="flex flex-col gap-3">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tx-3" />
            <input
              type="text"
              placeholder="Buscar por NF-e, fornecedor, setor ou usuário…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className={`${inputIris} w-full pl-9`}
            />
          </div>

          <div className="flex items-center gap-2">
            <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto pb-0.5 sm:flex-wrap sm:overflow-visible sm:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {STATUS_CHIPS.map((s) => (
                <button
                  key={s.label}
                  onClick={() => setStatusFiltro(s.value)}
                  className={chip(statusFiltro === s.value)}
                >
                  {s.label}
                </button>
              ))}
              {/* Eixo independente do status (uma conferência concluída pode ter pendência) —
                  tom warning quando ativo, em vez do accent sólido dos chips de status acima. */}
              <button
                onClick={() => setSomentePendencia((v) => !v)}
                className={`h-9 flex-shrink-0 rounded-iris px-3 text-sm font-medium transition-colors ${
                  somentePendencia ? 'bg-warning-soft text-warning' : 'bg-surface-2 text-tx-2 hover:bg-surface'
                }`}
              >
                Com pendência
              </button>
            </div>
            <button
              onClick={limparTudo}
              className="inline-flex h-9 flex-shrink-0 items-center rounded-iris px-3 text-sm font-medium text-tx-2 transition-colors hover:bg-surface-2"
            >
              Limpar
            </button>
          </div>
        </div>
      </div>

      {/* Lista */}
      <ListaConferencias
        conferencias={listaFiltrada}
        isLoading={isLoading}
        onSelect={(c) => navigate(`/conferencia/${c.id}`)}
      />
    </div>
  );
};

interface ListaConferenciasProps {
  conferencias: ConferenciaListItem[];
  isLoading: boolean;
  onSelect: (c: ConferenciaListItem) => void;
}

/** Tabela densa `md+` / cards `<md` — mesma fronteira e skeleton de XmlTerceirosList. */
const ListaConferencias: React.FC<ListaConferenciasProps> = ({ conferencias, isLoading, onSelect }) => {
  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-iris-lg border border-line bg-surface">
        <div className="divide-y divide-line">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <div className="h-3 w-10 animate-pulse rounded-iris-sm bg-surface-2" />
              <div className="h-3 flex-1 animate-pulse rounded-iris-sm bg-surface-2" />
              <div className="hidden h-3 w-24 animate-pulse rounded-iris-sm bg-surface-2 md:block" />
              <div className="hidden h-3 w-20 animate-pulse rounded-iris-sm bg-surface-2 md:block" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (conferencias.length === 0) {
    return (
      <div className="rounded-iris-lg border border-line bg-surface px-6 py-12 text-center">
        <ClipboardDocumentCheckIcon className="mx-auto h-10 w-10 text-tx-3" />
        <h3 className="mt-3 text-sm font-semibold text-tx">Nenhuma conferência encontrada</h3>
        <p className="mt-1 text-sm text-tx-3">Ajuste os filtros ou inicie uma nova conferência.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-iris-lg border border-line bg-surface">
      {/* Header da tabela densa — só de md pra cima. */}
      <div className="hidden border-b border-line bg-surface-2 px-4 py-2 md:block">
        <div className="grid grid-cols-12 gap-2 text-[11px] font-medium uppercase tracking-wide text-tx-3">
          <div className="col-span-1">NF-e</div>
          <div className="col-span-3">Fornecedor</div>
          <div className="col-span-2">Setor</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2">Divergência</div>
          <div className="col-span-2">Data</div>
          <div className="col-span-1">Usuário</div>
        </div>
      </div>

      <div className="divide-y divide-line">
        {conferencias.map((c) => {
          const status = badgeConferencia(c);
          return (
            <div
              key={c.id}
              onClick={() => onSelect(c)}
              className="cursor-pointer transition-colors hover:bg-surface-2"
            >
              {/* ── Mobile / tablet estreito (< md): card ── */}
              <div className="p-3 md:hidden">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-tx">NF-e {c.numeroNfe ?? '—'}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${status.badge}`}>
                        {status.label}
                      </span>
                    </div>
                  </div>
                  <div className="text-right text-[11px] tabular-nums text-tx-3">
                    {formatDateTime(c.criadoEm)}
                  </div>
                </div>

                <dl className="mt-2.5 space-y-1.5 text-[13px]">
                  <div className="flex items-center gap-2 text-tx">
                    <BuildingOffice2Icon className="h-4 w-4 flex-shrink-0 text-tx-3" />
                    <span className="truncate font-medium">{c.fornecedorNome || 'Fornecedor não informado'}</span>
                  </div>
                  {c.setorRecebimento && (
                    <div className="flex items-center gap-2 text-tx-3">
                      <MapPinIcon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{c.setorRecebimento}</span>
                    </div>
                  )}
                  {c.usuario && (
                    <div className="flex items-center gap-2 text-tx-3">
                      <UserIcon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{c.usuario}</span>
                    </div>
                  )}
                </dl>

                <div className="mt-3 border-t border-line pt-3">
                  <DivergenciaResumo resumo={c.resumo} />
                </div>
              </div>

              {/* ── Desktop (md+): linha densa ── */}
              <div className="hidden grid-cols-12 items-center gap-2 px-4 py-2 text-[13px] md:grid">
                <div className="col-span-1 min-w-0">
                  <div className="font-semibold text-tx">{c.numeroNfe ?? '—'}</div>
                </div>

                <div className="col-span-3 min-w-0">
                  <div className="truncate font-medium text-tx" title={c.fornecedorNome ?? undefined}>
                    {c.fornecedorNome || 'Não informado'}
                  </div>
                </div>

                <div className="col-span-2 min-w-0 truncate text-tx-2">{c.setorRecebimento || '—'}</div>

                <div className="col-span-1 min-w-0">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${status.badge}`}>
                    {status.label}
                  </span>
                </div>

                <div className="col-span-2 min-w-0">
                  <DivergenciaResumo resumo={c.resumo} />
                </div>

                <div className="col-span-2 min-w-0">
                  <div className="flex items-center gap-1 tabular-nums text-tx-2">
                    <CalendarIcon className="h-3 w-3 flex-shrink-0 text-tx-3" />
                    {formatDateTime(c.criadoEm)}
                  </div>
                </div>

                <div className="col-span-1 min-w-0 truncate text-tx-2" title={c.usuario ?? undefined}>
                  {c.usuario || '—'}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConferenciaListPage;
